import { MouseEventHandler, useContext, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import CircularProgress from '@mui/material/CircularProgress'
import styles from './styles.module.css'
import PrimaryButton from '../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../global/components/buttons/secondary-button/SecondaryButton'
import useFetch from '../../../../global/hooks/useFetchHook'
import { fetchData } from '../../../../global/utils/fetch'
import ToastNotification, {
  IAlertStatusProps,
} from '../../../../global/components/toast-alert/ToastNotification'
import { IUser } from '../../../../../../app/entities/User'
import { ILocation } from '../../../../../../app/entities/Location'
import { IOnCallTechnicianManagerOverride } from '../../../../../../app/entities/OnCallTechnicianManagerOverride'
import {
  getTechnicianManagers,
  setOnCallTechnicianManagerOverride,
  getAllLocationsWithCurrentManagers,
  getAllOnCallOverrides,
  deleteOnCallOverride,
  updateOnCallOverride,
} from '../api'
import { OVERRIDE_STATUS } from '../../../../global/constants/on-call-override'
import { UserContext } from '../../../../base/components/base-container/BaseContainer'
import hasPermission from '../../../../global/utils/user/has-permission'
import { PERMISSIONS } from '../../../../global/constants/permissions'

interface IOnCallTechnicianManagerModalProps {
  isOpen: boolean
  handleClosed: MouseEventHandler
}

interface IFormData {
  technicianManagerObjectId: string
  fromDate: string
  toDate: string
}

export default function OnCallTechnicianManagerModal({
  isOpen,
  handleClosed,
}: IOnCallTechnicianManagerModalProps) {
  const { user } = useContext(UserContext)
  const canEdit = hasPermission(user).edit(
    PERMISSIONS.TECHNICIANS.CATEGORY,
    PERMISSIONS.TECHNICIANS.ENTRIES.ON_CALL_TECHNICIAN_MANAGER.NAME,
  )
  // Helper function to format date from YYYY-MM-DD to MM/DD/YYYY
  const formatDate = (dateString: string): string => {
    if (!dateString) return ''
    const [year, month, day] = dateString.split('-')
    return `${month}/${day}/${year}`
  }

  // Helper function to compute override status
  const getOverrideStatus = (
    override: IOnCallTechnicianManagerOverride,
  ): string => {
    const from = new Date(override.fromDate)
    const to = new Date(override.toDate)
    const now = new Date()

    if (now < from) {
      return OVERRIDE_STATUS.SCHEDULED
    }

    if (now >= from && now <= to) {
      return OVERRIDE_STATUS.ACTIVE_NOW
    }

    return OVERRIDE_STATUS.SCHEDULED
  }

  const [activeTab, setActiveTab] = useState<number>(0)
  const [formData, setFormData] = useState<IFormData>({
    technicianManagerObjectId: '',
    fromDate: '',
    toDate: '',
  })
  const [editingOverrideObjectId, setEditingOverrideObjectId] = useState<
    string | null
  >(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [alertStatus, setAlertStatus] = useState<IAlertStatusProps>({
    isOpen: false,
    message: '',
    severity: 'success',
  })

  // Fetch users with 'tech-manager' role
  const { data: technicianManagers, isLoading } = useFetch<IUser[]>(
    getTechnicianManagers(),
  )

  // Fetch all locations with their current managers
  const { data: locationsWithManagers, isLoading: isLoadingLocations } =
    useFetch<ILocation[]>(getAllLocationsWithCurrentManagers())

  // Fetch all on-call overrides
  const { data: onCallOverrides, isLoading: isLoadingOverrides } = useFetch<{
    currentOnCallTechnicianManagerOverride:
      | IOnCallTechnicianManagerOverride
      | undefined
    allOnCallTechnicianManagerOverrides: IOnCallTechnicianManagerOverride[]
    isOnCallTime: boolean
  }>(getAllOnCallOverrides())

  // Manual data state for refreshing after save/delete
  const [manualLocationsData, setManualLocationsData] = useState<
    ILocation[] | null
  >(null)
  const [manualOverridesData, setManualOverridesData] = useState<{
    currentOnCallTechnicianManagerOverride:
      | IOnCallTechnicianManagerOverride
      | undefined
    allOnCallTechnicianManagerOverrides: IOnCallTechnicianManagerOverride[]
    isOnCallTime: boolean
  } | null>(null)

  // Function to manually refresh data
  const refreshData = async () => {
    try {
      const [newLocations, newOverrides] = await Promise.all([
        fetchData<ILocation[]>(getAllLocationsWithCurrentManagers()),
        fetchData<{
          currentOnCallTechnicianManagerOverride:
            | IOnCallTechnicianManagerOverride
            | undefined
          allOnCallTechnicianManagerOverrides: IOnCallTechnicianManagerOverride[]
          isOnCallTime: boolean
        }>(getAllOnCallOverrides()),
      ])
      setManualLocationsData(newLocations)
      setManualOverridesData(newOverrides)
    } catch (error) {
      console.error('Failed to refresh data:', error)
    }
  }

  // Use manual data if available, otherwise use useFetch data
  const displayLocations = manualLocationsData ?? locationsWithManagers
  const displayOverrides = manualOverridesData ?? onCallOverrides

  const handleAlertClosed = () => {
    setAlertStatus({ ...alertStatus, isOpen: false })
  }

  const isFormValid = (): boolean => {
    if (
      !formData.technicianManagerObjectId ||
      !formData.fromDate ||
      !formData.toDate
    ) {
      return false
    }

    const fromDate = new Date(formData.fromDate)
    const toDate = new Date(formData.toDate)

    return toDate >= fromDate
  }

  const handleEdit = (override: IOnCallTechnicianManagerOverride) => {
    setEditingOverrideObjectId(override.objectId)
    setFormData({
      technicianManagerObjectId: override.technicianManager.objectId,
      fromDate: override.fromDate,
      toDate: override.toDate,
    })
    setActiveTab(1)
  }

  const resetForm = () => {
    setEditingOverrideObjectId(null)
    setFormData({
      technicianManagerObjectId: '',
      fromDate: '',
      toDate: '',
    })
  }

  const handleSave = async () => {
    if (!isFormValid()) {
      setAlertStatus({
        isOpen: true,
        message:
          'Please fill in all required fields and ensure dates are valid',
        severity: 'error',
      })
      return
    }

    setIsSubmitting(true)
    try {
      if (editingOverrideObjectId) {
        await fetchData(
          updateOnCallOverride(editingOverrideObjectId, formData),
        )
      } else {
        await fetchData(setOnCallTechnicianManagerOverride(formData))
      }

      // Refresh the data
      await refreshData()

      // Reset form
      resetForm()

      setAlertStatus({
        isOpen: true,
        message: editingOverrideObjectId
          ? 'On-call technician manager override updated successfully'
          : 'On-call technician manager override saved successfully',
        severity: 'success',
      })

      // Switch to "Current Assignments" tab to show the override
      setActiveTab(0)
    } catch (error: any) {
      console.error('Save failed:', error)
      const errorMessage =
        error?.message ||
        error?.toString() ||
        'Failed to save on-call technician manager override'
      setAlertStatus({
        isOpen: true,
        message: `Failed to save: ${errorMessage}`,
        severity: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (
    overrideObjectId: string,
    managerName: string,
  ) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the override for ${managerName}?`,
      )
    ) {
      return
    }

    try {
      await fetchData(deleteOnCallOverride(overrideObjectId))

      // Refresh the data
      await refreshData()

      setAlertStatus({
        isOpen: true,
        message: 'Override deleted successfully',
        severity: 'success',
      })
    } catch (error: any) {
      console.error('Delete failed:', error)
      const errorMessage =
        error?.message || error?.toString() || 'Failed to delete override'
      setAlertStatus({
        isOpen: true,
        message: `Failed to delete: ${errorMessage}`,
        severity: 'error',
      })
    }
  }

  const handleFormChange = (field: keyof IFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={(e) => {
          resetForm()
          handleClosed(e as any)
        }}
      >
        <Box className={styles.modalContainer}>
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>
              On-Call Technician Manager Override
            </h2>
            <p className={styles.modalDescription}>
              Create and Manage On-Call Technician Managers.
            </p>

            <div className={styles.tabContainer}>
              {activeTab === 0 ? (
                <PrimaryButton
                  buttonName='View Managers'
                  onClick={() => setActiveTab(0)}
                />
              ) : (
                <SecondaryButton
                  buttonName='View Managers'
                  onClick={() => setActiveTab(0)}
                />
              )}
              {activeTab === 1 ? (
                <PrimaryButton
                  buttonName={
                    editingOverrideObjectId
                      ? 'Edit Override'
                      : 'Create Override'
                  }
                  onClick={() => setActiveTab(1)}
                />
              ) : (
                <SecondaryButton
                  buttonName='Create Override'
                  onClick={() => {
                    resetForm()
                    setActiveTab(1)
                  }}
                  disabled={!canEdit}
                />
              )}
            </div>

            <div className={styles.contentArea}>
              {activeTab === 0 ? (
                <div>
                  {isLoadingLocations || isLoadingOverrides ? (
                    <div className={styles.loadingContainer}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <>
                      {/* Current On-Call Manager Section */}
                      {displayOverrides?.allOnCallTechnicianManagerOverrides &&
                      displayOverrides.allOnCallTechnicianManagerOverrides.length >
                        0 ? (
                        <div className={styles.currentOnCallSection}>
                          <h3 className={styles.sectionTitle}>
                            Current On-Call Manager
                          </h3>
                          <div className={styles.currentOnCallInfo}>
                            {(() => {
                              // Find override that covers today's date
                              const today = new Date()
                              const currentOverride =
                                displayOverrides.allOnCallTechnicianManagerOverrides.find(
                                  (o: IOnCallTechnicianManagerOverride) => {
                                    const from = new Date(o.fromDate)
                                    const to = new Date(o.toDate)
                                    return today >= from && today <= to
                                  },
                                )

                              if (!currentOverride) {
                                return (
                                  <p className={styles.modalDescription}>
                                    No override scheduled. Technicians use their
                                    location managers.
                                  </p>
                                )
                              }

                              return (
                                <div
                                  {...(canEdit && {
                                    className: styles.clickableRow,
                                    onClick: () => handleEdit(currentOverride),
                                    title: 'Click to edit this override',
                                  })}
                                >
                                  <p>
                                    <strong>
                                      {currentOverride.technicianManager.name}
                                    </strong>
                                  </p>
                                  <p className={styles.modalDescription}>
                                    Active:{' '}
                                    {formatDate(currentOverride.fromDate)} -{' '}
                                    {formatDate(currentOverride.toDate)}
                                  </p>
                                  {displayOverrides.isOnCallTime && (
                                    <span className={styles.overrideBadge}>
                                      ON-CALL HOURS
                                    </span>
                                  )}
                                </div>
                              )
                            })()}
                          </div>
                        </div>
                      ) : (
                        <div className={styles.currentOnCallSection}>
                          <h3 className={styles.sectionTitle}>
                            Current On-Call Manager
                          </h3>
                          <p className={styles.modalDescription}>
                            No override scheduled. Technicians use their
                            location managers.
                          </p>
                        </div>
                      )}

                      {/* Location Managers Table */}
                      <div className={styles.sectionDivider}>
                        <h3 className={styles.sectionTitle}>
                          Technician Managers by Location
                        </h3>
                        <p className={styles.modalDescription}>
                          Default technician managers for each location (used
                          during business hours).
                        </p>
                        {displayLocations && displayLocations.length > 0 ? (
                          <table className={styles.assignmentsTable}>
                            <thead>
                              <tr>
                                <th>Location</th>
                                <th>Technician Manager</th>
                              </tr>
                            </thead>
                            <tbody>
                              {displayLocations.map((location) => (
                                <tr key={location.objectId}>
                                  <td>{location.name}</td>
                                  <td>
                                    {location.technicianManager?.name || 'None'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p>No locations found</p>
                        )}
                      </div>

                      {/* Scheduled Overrides Table */}
                      {displayOverrides?.allOnCallTechnicianManagerOverrides &&
                      displayOverrides.allOnCallTechnicianManagerOverrides
                        .length > 0 ? (
                        <div className={styles.sectionDivider}>
                          <h3 className={styles.sectionTitle}>
                            Scheduled Overrides
                          </h3>
                          <p className={styles.modalDescription}>
                            All scheduled on-call manager overrides (active
                            during after-hours and weekends only).
                          </p>
                          <table className={styles.assignmentsTable}>
                            <thead>
                              <tr>
                                <th>Manager</th>
                                <th>From Date</th>
                                <th>To Date</th>
                                <th>Status</th>
                                {canEdit && <th>Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {displayOverrides.allOnCallTechnicianManagerOverrides.map(
                                (
                                  override: IOnCallTechnicianManagerOverride,
                                ) => (
                                  <tr
                                    key={override.objectId}
                                    {...(canEdit && {
                                      className: styles.clickableRow,
                                      onClick: () => handleEdit(override),
                                      title: 'Click to edit this override',
                                    })}
                                  >
                                    <td>{override.technicianManager.name}</td>
                                    <td>{formatDate(override.fromDate)}</td>
                                    <td>{formatDate(override.toDate)}</td>
                                    <td>
                                      {getOverrideStatus(override) ===
                                      OVERRIDE_STATUS.ACTIVE_NOW ? (
                                        <span className={styles.overrideBadge}>
                                          {getOverrideStatus(override)}
                                        </span>
                                      ) : (
                                        <span>{getOverrideStatus(override)}</span>
                                      )}
                                    </td>
                                    {canEdit && (
                                      <td>
                                        <SecondaryButton
                                          buttonName='Delete'
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            handleDelete(
                                              override.objectId,
                                              override.technicianManager.name,
                                            )
                                          }}
                                        />
                                      </td>
                                    )}
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              ) : (
                <>
                  {isLoading ? (
                    <div className={styles.progressContainer}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <div className={styles.formContainer}>
                      <p className={styles.modalDescription}>
                        This manager will handle ALL technicians during
                        after-hours (5pm-4am EST) and weekends within the
                        selected date range.
                      </p>

                      <div className={styles.inputField}>
                        <TextField
                          select
                          label='Select Technician Manager *'
                          value={formData.technicianManagerObjectId}
                          onChange={(e) =>
                            handleFormChange(
                              'technicianManagerObjectId',
                              e.target.value,
                            )
                          }
                          fullWidth
                          disabled={
                            !technicianManagers ||
                            technicianManagers.length === 0
                          }
                          InputLabelProps={{ shrink: true }}
                        >
                          {technicianManagers &&
                          technicianManagers.length > 0 ? (
                            technicianManagers.map((manager) => (
                              <MenuItem
                                key={manager.objectId}
                                value={manager.objectId}
                              >
                                {manager.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value=''>
                              No technician managers available
                            </MenuItem>
                          )}
                        </TextField>
                      </div>

                      <div className={styles.dateFieldsContainer}>
                        <div className={styles.dateField}>
                          <TextField
                            type='date'
                            label='From Date *'
                            name='fromDate'
                            value={formData.fromDate}
                            onChange={(e) =>
                              handleFormChange('fromDate', e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </div>

                        <div className={styles.dateField}>
                          <TextField
                            type='date'
                            label='To Date *'
                            name='toDate'
                            value={formData.toDate}
                            onChange={(e) =>
                              handleFormChange('toDate', e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className={styles.buttonsContainer}>
              <SecondaryButton
                buttonName='Cancel'
                onClick={(e) => {
                  resetForm()
                  handleClosed(e)
                }}
                disabled={isSubmitting}
              />
              {activeTab === 1 && (
                <PrimaryButton
                  buttonName={
                    isSubmitting
                      ? 'Saving...'
                      : editingOverrideObjectId
                        ? 'Update Override'
                        : 'Save Override'
                  }
                  onClick={handleSave}
                  disabled={isSubmitting || !isFormValid()}
                />
              )}
            </div>
          </div>
        </Box>
      </Modal>

      <ToastNotification
        open={alertStatus.isOpen}
        message={alertStatus.message}
        severity={alertStatus.severity}
        handleClose={handleAlertClosed}
      />
    </>
  )
}
